<template>
  <AIVDialog
    v-model="isVisible"
    :title="isEditMode ? 'Edit Time Off Request' : 'Request Time Off'"
    width="650px"
    height="auto"
    class="time-off-modal"
  >
    <form @submit.prevent="submitRequest">
      <!-- Request Type Selector -->
      <div class="row">
        <div class="col-md-12 mb-3">
          <label class="form-label">Request Type *</label>
          <div class="request-type-selector">
            <el-button
              :type="request.request_type === 'single_full_day' ? 'primary' : 'default'"
              @click="setRequestType('single_full_day')"
              class="request-type-button"
            >
              <i class="fa fa-calendar-day"></i>
              Single Full Day
            </el-button>
            <el-button
              :type="request.request_type === 'single_partial_day' ? 'primary' : 'default'"
              @click="setRequestType('single_partial_day')"
              class="request-type-button"
            >
              <i class="fa fa-clock"></i>
              Partial Day
            </el-button>
            <el-button
              :type="request.request_type === 'multiple_full_days' ? 'primary' : 'default'"
              @click="setRequestType('multiple_full_days')"
              class="request-type-button"
            >
              <i class="fa fa-calendar-week"></i>
              Multiple Days
            </el-button>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6 mb-3">
          <label class="form-label">
            {{ request.request_type === 'multiple_full_days' ? 'Start Date *' : 'Date *' }}
          </label>
          <el-input
            v-model="request.start_date"
            type="date"
            :min="minDate"
            @change="validateDates"
          />
        </div>
        <div class="col-md-6 mb-3" v-if="request.request_type === 'multiple_full_days'">
          <label class="form-label">End Date *</label>
          <el-input
            v-model="request.end_date"
            type="date"
            :min="request.start_date || minDate"
            @change="validateDates"
          />
        </div>
      </div>

      <!-- Time Pickers for Partial Day -->
      <div class="row" v-if="request.request_type === 'single_partial_day'">
        <div class="col-md-6 mb-3">
          <label class="form-label">Start Time *</label>
          <AIVTimeSelect
            v-model="request.start_time"
            :minuteInterval="15"
            @input="validateTimes"
          />
        </div>
        <div class="col-md-6 mb-3">
          <label class="form-label">End Time *</label>
          <AIVTimeSelect
            v-model="request.end_time"
            :minuteInterval="15"
            @input="validateTimes"
          />
        </div>
      </div>

      <div class="row" v-if="request.start_date && (request.request_type !== 'multiple_full_days' || request.end_date)">
        <div class="col-md-12 mb-3">
          <div class="alert alert-info">
            <i class="fa fa-info-circle"></i>
            <span v-if="request.request_type === 'single_partial_day'">
              <strong>Partial day</strong> - {{ formatTimeRange }}
            </span>
            <span v-else>
              <strong>{{ numberOfDays }}</strong> day{{ numberOfDays > 1 ? 's' : '' }} requested
            </span>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12 mb-3">
          <label class="form-label">Reason (Optional)</label>
          <el-input
            v-model="request.reason"
            type="textarea"
            :rows="4"
            placeholder="Please provide a reason for your time off request..."
          />
        </div>
      </div>

      <!-- Warning about affected shifts -->
      <div class="row" v-if="affectedShiftsCount > 0">
        <div class="col-md-12 mb-3">
          <div class="alert alert-warning">
            <i class="fa fa-exclamation-triangle"></i>
            This time off period overlaps with <strong>{{ affectedShiftsCount }}</strong>
            scheduled shift{{ affectedShiftsCount > 1 ? 's' : '' }}.
            <br>
            <small>Shifts will be unassigned if the request is approved.</small>
          </div>
        </div>
      </div>

      <!-- Validation errors -->
      <div class="row" v-if="validationError">
        <div class="col-md-12 mb-3">
          <div class="alert alert-danger">
            <i class="fa fa-times-circle"></i>
            {{ validationError }}
          </div>
        </div>
      </div>

      <!-- No Admin Warning -->
      <div class="row" v-if="showNoAdminWarning">
        <div class="col-md-12 mb-3">
          <div class="alert alert-warning">
            <i class="fa fa-exclamation-triangle"></i>
            <strong>No Admin Assigned:</strong>
            You can submit this request, but it won't be visible for approval until an admin is added to your profile. Please contact your manager.
          </div>
        </div>
      </div>
    </form>

    <template slot="footer">
      <div class="text-center">
        <button
          @click="submitRequest"
          :disabled="!isFormValid || isSubmitting"
          class="btn btn-primary"
        >
          <i class="fa fa-paper-plane"></i>
          {{ isSubmitting ? 'Submitting...' : 'Submit Request' }}
        </button>
        <button
          @click="closeModal"
          class="btn btn-secondary ml-2"
          :disabled="isSubmitting"
        >
          Cancel
        </button>
      </div>
    </template>
  </AIVDialog>
</template>

<script>
import { timeOffRequestsService } from 'src/serviceHandlers/timeOffRequestsService';
import { shiftsService } from 'src/serviceHandlers/shiftsService';
import { useLoginStore } from 'src/stores/loginStore';
import AIVTimeSelect from 'src/components/AIVTimeSelect.vue';
import { localDateString } from '@/utils/dateOnly';

export default {
  name: "TimeOffRequestModal",
  components: {
    AIVTimeSelect
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    editData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      request: {
        request_type: 'single_full_day',
        start_date: '',
        end_date: '',
        start_time: '',
        end_time: '',
        reason: ''
      },
      validationError: '',
      affectedShiftsCount: 0,
      isSubmitting: false,
      loginStore: useLoginStore()
    };
  },
  computed: {
    isVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    isEditMode() {
      return this.editData && this.editData.id;
    },
    isFormValid() {
      return this.request.start_date &&
             this.request.end_date &&
             !this.validationError;
    },
    minDate() {
      // Can't request time off for past dates (local today — the UTC date
      // rolls to tomorrow after ~6pm Denver and would block requesting today)
      return localDateString();
    },
    showNoAdminWarning() {
      // Show warning if user is not an admin and doesn't have an admin assigned
      const user = this.loginStore.user;
      return user && user.user_type !== 'Admin' && !user.id_admin;
    },
    numberOfDays() {
      if (this.request.request_type === 'single_partial_day') {
        return 1; // Partial day is always 1 day
      }

      const startDate = this.request.request_type === 'single_full_day' ? this.request.start_date : this.request.start_date;
      const endDate = this.request.request_type === 'single_full_day' ? this.request.start_date : this.request.end_date;

      if (!startDate || !endDate) return 0;
      // Parse dates as local dates to avoid timezone issues
      const [startYear, startMonth, startDay] = startDate.split('-');
      const [endYear, endMonth, endDay] = endDate.split('-');
      const start = new Date(startYear, startMonth - 1, startDay);
      const end = new Date(endYear, endMonth - 1, endDay);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // Include both start and end dates
      return diffDays;
    },
    formatTimeRange() {
      if (!this.request.start_time || !this.request.end_time) return '';
      return `${this.formatTime(this.request.start_time)} - ${this.formatTime(this.request.end_time)}`;
    }
  },
  watch: {
    value(newVal) {
      if (newVal && this.editData) {
        // Load data for editing
        this.request = {
          request_type: this.editData.request_type || 'multiple_full_days',
          start_date: this.editData.start_date,
          end_date: this.editData.end_date,
          start_time: this.editData.start_time ? this.editData.start_time.substring(0, 5) : '',
          end_time: this.editData.end_time ? this.editData.end_time.substring(0, 5) : '',
          reason: this.editData.reason || ''
        };
      }
    },
    'request.start_date': 'checkAffectedShifts',
    'request.end_date': 'checkAffectedShifts',
    'request.start_time': 'checkAffectedShifts',
    'request.end_time': 'checkAffectedShifts'
  },
  methods: {
    setRequestType(type) {
      this.request.request_type = type;
      this.handleRequestTypeChange();
    },

    handleRequestTypeChange() {
      // When switching request types, sync dates appropriately
      if (this.request.request_type === 'single_full_day' || this.request.request_type === 'single_partial_day') {
        // For single day requests, ensure start and end dates are the same
        if (this.request.start_date) {
          this.request.end_date = this.request.start_date;
        }
      }

      // Clear times if not partial day
      if (this.request.request_type !== 'single_partial_day') {
        this.request.start_time = '';
        this.request.end_time = '';
      }

      this.validateDates();
    },

    validateDates() {
      this.validationError = '';

      if (!this.request.start_date) {
        return;
      }

      // For single day requests, sync end_date with start_date
      if (this.request.request_type === 'single_full_day' || this.request.request_type === 'single_partial_day') {
        this.request.end_date = this.request.start_date;
      }

      if (this.request.request_type === 'multiple_full_days' && !this.request.end_date) {
        return;
      }

      // Parse dates as local dates to avoid timezone issues
      const [startYear, startMonth, startDay] = this.request.start_date.split('-');
      const start = new Date(startYear, startMonth - 1, startDay);

      if (this.request.end_date) {
        const [endYear, endMonth, endDay] = this.request.end_date.split('-');
        const end = new Date(endYear, endMonth - 1, endDay);

        if (end < start) {
          this.validationError = 'End date must be on or after start date';
          return;
        }
      }

      // Check if trying to request too far in the future (optional limit)
      const maxFutureDays = 365; // 1 year
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset to midnight for fair comparison
      const daysDiff = (start - today) / (1000 * 60 * 60 * 24);

      if (daysDiff > maxFutureDays) {
        this.validationError = `Time off requests cannot be more than ${maxFutureDays} days in the future`;
      }
    },

    validateTimes() {
      this.validationError = '';

      if (this.request.request_type !== 'single_partial_day') {
        return;
      }

      if (!this.request.start_time || !this.request.end_time) {
        return;
      }

      // Compare times
      if (this.request.end_time <= this.request.start_time) {
        this.validationError = 'End time must be after start time';
      }
    },

    formatTime(timeString) {
      if (!timeString) return '';
      // Convert 24-hour time to 12-hour format
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    },

    async checkAffectedShifts() {
      // Check how many shifts would be affected
      if (!this.request.start_date || !this.request.end_date) {
        this.affectedShiftsCount = 0;
        return;
      }

      try {
        const userId = this.loginStore.user.id;

        // Get shifts for this caregiver in the date range
        const response = await shiftsService.getByCaregiver({
          caregiver_id: userId,
          start_date: this.request.start_date,
          end_date: this.request.end_date
        });

        this.affectedShiftsCount = response.data?.length || 0;
      } catch (error) {
        this.affectedShiftsCount = 0;
      }
    },

    async submitRequest() {
      if (!this.isFormValid || this.isSubmitting) {
        return;
      }

      this.isSubmitting = true;

      try {
        const payload = {
          request_type: this.request.request_type,
          start_date: this.request.start_date,
          end_date: this.request.end_date,
          reason: this.request.reason || null
        };

        // Add time fields for partial day requests
        if (this.request.request_type === 'single_partial_day') {
          payload.start_time = this.request.start_time + ':00'; // Add seconds
          payload.end_time = this.request.end_time + ':00'; // Add seconds
        }

        if (this.isEditMode) {
          // Update existing request
          await timeOffRequestsService.update(this.editData.id, payload);
          this.$notifySuccess("Time off request updated successfully");
        } else {
          // Create new request
          await timeOffRequestsService.create(payload);
          this.$notifySuccess("Time off request submitted successfully");
        }

        this.$emit('saved');
        this.closeModal();
      } catch (error) {
        const errorMessage = error.response?.data?.error || error.message || 'Error submitting request';
        this.$notifyError(errorMessage);
      } finally {
        this.isSubmitting = false;
      }
    },

    closeModal() {
      this.isVisible = false;
      this.resetForm();
    },

    resetForm() {
      this.request = {
        request_type: 'single_full_day',
        start_date: '',
        end_date: '',
        start_time: '',
        end_time: '',
        reason: ''
      };
      this.validationError = '';
      this.affectedShiftsCount = 0;
      this.isSubmitting = false;
    }
  }
};
</script>

<style scoped>
.request-type-selector {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.request-type-button {
  flex: 1;
  min-width: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
}

.request-type-button i {
  font-size: 1.1em;
}

.form-label {
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #d9d9d9;
}

/* Element UI input styling to match AIVRow */
:deep(.el-input__inner) {
  background-color: #656565 !important;
  color: #ffffff !important;
  border-radius: 6px !important;
  height: 40px !important;
  font-size: 14px !important;
  font-weight: normal !important;
}

:deep(.el-input__inner:focus) {
  border-color: #ffffff !important;
  box-shadow: none !important;
  background-color: #656565 !important;
  color: #ffffff !important;
}

:deep(.el-textarea__inner) {
  background-color: #656565 !important;
  color: #ffffff !important;
  font-size: 14px !important;
  font-weight: normal !important;
  word-break: normal;
  overflow-wrap: break-word;
  white-space: pre-wrap;
}

:deep(.el-textarea__inner:focus) {
  border-color: #ffffff !important;
  box-shadow: none !important;
  background-color: #656565 !important;
  color: #ffffff !important;
  font-size: 14px !important;
  font-weight: normal !important;
}

.alert {
  border-radius: 4px;
  padding: 12px 16px;
  margin-bottom: 0;
}

.alert-info {
  background-color: rgba(23, 162, 184, 0.15);
  border-color: #17a2b8;
  color: #17a2b8;
}

.alert-warning {
  background-color: rgba(184, 134, 11, 0.15);
  border-color: #b8860b;
  color: #daa520;
}

.alert-danger {
  background-color: rgba(220, 53, 69, 0.15);
  border-color: #dc3545;
  color: #dc3545;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background-color: #007bff;
  border-color: #007bff;
}

.btn-primary:hover:not(:disabled) {
  background-color: #0056b3;
  border-color: #0056b3;
}

.btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #5a6268;
  border-color: #545b62;
}
</style>
